/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  RevokedSessionsDto,
  SessionDto,
  UpdateOnboardingDto,
  UserDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary The signed-in user.
 */
export const getMe = (signal?: AbortSignal) => {
  return customInstance<UserDto>({ url: `/api/v1/me`, method: 'GET', signal });
};

export const getGetMeQueryKey = () => {
  return [`/api/v1/me`] as const;
};

export const getGetMeQueryOptions = <
  TData = Awaited<ReturnType<typeof getMe>>,
  TError = ErrorType<ApiErrorDto>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMeQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMe>>> = ({ signal }) =>
    getMe(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMe>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMeQueryResult = NonNullable<Awaited<ReturnType<typeof getMe>>>;
export type GetMeQueryError = ErrorType<ApiErrorDto>;

export function useGetMe<
  TData = Awaited<ReturnType<typeof getMe>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMe>>,
          TError,
          Awaited<ReturnType<typeof getMe>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMe<
  TData = Awaited<ReturnType<typeof getMe>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMe>>,
          TError,
          Awaited<ReturnType<typeof getMe>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMe<
  TData = Awaited<ReturnType<typeof getMe>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary The signed-in user.
 */

export function useGetMe<
  TData = Awaited<ReturnType<typeof getMe>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMe>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMeQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Save the signed-in user's onboarding progress (forward only).
 */
export const updateOnboarding = (
  updateOnboardingDto: BodyType<UpdateOnboardingDto>,
  signal?: AbortSignal,
) => {
  return customInstance<UserDto>({
    url: `/api/v1/me/onboarding`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateOnboardingDto,
    signal,
  });
};

export const getUpdateOnboardingMutationKey = () => ['updateOnboarding'] as const;

export const getUpdateOnboardingMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOnboarding>>,
    TError,
    UpdateOnboardingMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOnboarding>>,
  TError,
  UpdateOnboardingMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateOnboardingMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOnboarding>>,
    UpdateOnboardingMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return updateOnboarding(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOnboardingMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOnboarding>>
>;
export type UpdateOnboardingMutationBody = BodyType<UpdateOnboardingDto>;
export type UpdateOnboardingMutationError = ErrorType<ApiErrorDto>;
export type UpdateOnboardingMutationVariables = { data: BodyType<UpdateOnboardingDto> };

/**
 * @summary Save the signed-in user's onboarding progress (forward only).
 */
export const useUpdateOnboarding = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateOnboarding>>,
      TError,
      UpdateOnboardingMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateOnboarding>>,
  TError,
  UpdateOnboardingMutationVariables,
  TContext
> => {
  return useMutation(getUpdateOnboardingMutationOptions(options), queryClient);
};
/**
 * @summary Where the signed-in user is signed in, most recently active first.
 */
export const listMySessions = (signal?: AbortSignal) => {
  return customInstance<SessionDto[]>({
    url: `/api/v1/me/sessions`,
    method: 'GET',
    signal,
  });
};

export const getListMySessionsQueryKey = () => {
  return [`/api/v1/me/sessions`] as const;
};

export const getListMySessionsQueryOptions = <
  TData = Awaited<ReturnType<typeof listMySessions>>,
  TError = ErrorType<ApiErrorDto>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof listMySessions>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListMySessionsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listMySessions>>> = ({
    signal,
  }) => listMySessions(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listMySessions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListMySessionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listMySessions>>
>;
export type ListMySessionsQueryError = ErrorType<ApiErrorDto>;

export function useListMySessions<
  TData = Awaited<ReturnType<typeof listMySessions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMySessions>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMySessions>>,
          TError,
          Awaited<ReturnType<typeof listMySessions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMySessions<
  TData = Awaited<ReturnType<typeof listMySessions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMySessions>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMySessions>>,
          TError,
          Awaited<ReturnType<typeof listMySessions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListMySessions<
  TData = Awaited<ReturnType<typeof listMySessions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMySessions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Where the signed-in user is signed in, most recently active first.
 */

export function useListMySessions<
  TData = Awaited<ReturnType<typeof listMySessions>>,
  TError = ErrorType<ApiErrorDto>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listMySessions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListMySessionsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Sign out everywhere but here.
 */
export const revokeMyOtherSessions = (signal?: AbortSignal) => {
  return customInstance<RevokedSessionsDto>({
    url: `/api/v1/me/sessions`,
    method: 'DELETE',
    signal,
  });
};

export const getRevokeMyOtherSessionsMutationKey = () =>
  ['revokeMyOtherSessions'] as const;

export const getRevokeMyOtherSessionsMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeMyOtherSessions>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeMyOtherSessions>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = getRevokeMyOtherSessionsMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeMyOtherSessions>>,
    void
  > = () => {
    return revokeMyOtherSessions();
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeMyOtherSessionsMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeMyOtherSessions>>
>;

export type RevokeMyOtherSessionsMutationError = ErrorType<ApiErrorDto>;

/**
 * @summary Sign out everywhere but here.
 */
export const useRevokeMyOtherSessions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeMyOtherSessions>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeMyOtherSessions>>,
  TError,
  void,
  TContext
> => {
  return useMutation(getRevokeMyOtherSessionsMutationOptions(options), queryClient);
};
/**
 * @summary Sign out one other browser or device. 400 for the current session.
 */
export const revokeMySession = (sessionId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/me/sessions/${sessionId}`,
    method: 'DELETE',
    signal,
  });
};

export const getRevokeMySessionMutationKey = () => ['revokeMySession'] as const;

export const getRevokeMySessionMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeMySession>>,
    TError,
    RevokeMySessionMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeMySession>>,
  TError,
  RevokeMySessionMutationVariables,
  TContext
> => {
  const mutationKey = getRevokeMySessionMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeMySession>>,
    RevokeMySessionMutationVariables
  > = (props) => {
    const { sessionId } = props ?? {};

    return revokeMySession(sessionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeMySessionMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeMySession>>
>;

export type RevokeMySessionMutationError = ErrorType<ApiErrorDto>;
export type RevokeMySessionMutationVariables = { sessionId: string };

/**
 * @summary Sign out one other browser or device. 400 for the current session.
 */
export const useRevokeMySession = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeMySession>>,
      TError,
      RevokeMySessionMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeMySession>>,
  TError,
  RevokeMySessionMutationVariables,
  TContext
> => {
  return useMutation(getRevokeMySessionMutationOptions(options), queryClient);
};
